import { useEffect } from "react";

import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { useSpecialtyOptionsForLicenceOrganizationRule } from "@/Common/CustomHooks/User/useSpecialtyOptionsForLicenceOrganizationRule";
import { useTitleOptionsForSelectedOrganization } from "@/Common/CustomHooks/User/useTitleOptionsForSelectedOrganization";
import { FilteredOrganizationRules } from "@/Common/Utilities/FilteredOrganizationRules";
import { OrganizationComboBox } from "./OrganizationComboBox";
import { SpecialtyComboBox } from "./SpecialtyComboBox";
import { TitleComboBox } from "./TitleComboBox";

interface ITitleAndSpecialtySelectionProps {
    filteredOrganizationRules: FilteredOrganizationRules;
    selectedOrganizationIdentifier: string | null;
    title: LicenceOrganisationRule | null;
    specialty: LicenceOrganisationSpecialisation | null;
    onOrganizationSelect: (organizationIdentifier: string) => void;
    onTitleSelect: (title: LicenceOrganisationRule) => void;
    onSpecialtySelect: (specialty: LicenceOrganisationSpecialisation) => void;

    isTitleRequired?: boolean;
    isSpecialtyRequired?: boolean;
    renderContainer?: (children: JSX.Element[]) => JSX.Element;
    renderItemContainer?: (child: JSX.Element, index: number) => JSX.Element;

    isOrganisationValid: boolean;
    isTitleValid: boolean;
    isSpecialtyValid: boolean;
}

function TitleAndSpecialtySelection(props: ITitleAndSpecialtySelectionProps): JSX.Element {
    const titleOptions = useTitleOptionsForSelectedOrganization(props.filteredOrganizationRules, props.selectedOrganizationIdentifier);
    const specialtyOptions = useSpecialtyOptionsForLicenceOrganizationRule(props.title);

    useEffect(
        () => {
            if (titleOptions.length === 1)
                props.onTitleSelect(titleOptions[0]);
        },
        // The title data comes from the cache and should be static
        // Only update when the title options change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [titleOptions.map(p => p.Id).join()]
    );

    useEffect(
        () => {
            if (specialtyOptions.length === 1)
                props.onSpecialtySelect(specialtyOptions[0]);
        },
        // The specialty data comes from the cache and should be static
        // Only update when the specialty options change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [specialtyOptions.map(p => p.Id).join()]
    );

    const organizationComboBox = (
        <OrganizationComboBox
            filteredOrganizationRules={props.filteredOrganizationRules}
            selectedOrganizationIdentifier={props.selectedOrganizationIdentifier}
            onOrganizationSelect={props.onOrganizationSelect}
            isRequired={props.isTitleRequired} // title selection depends on organization selection
            isValid={props.isOrganisationValid}
        />
    );

    const titleComboBox = (
        <TitleComboBox
            titleOptions={titleOptions}
            title={props.title}
            onTitleSelect={props.onTitleSelect}
            isRequired={props.isTitleRequired}
            isValid={props.isTitleValid}
        />
    );

    const specialtyComboBox = (
        <SpecialtyComboBox
            specialtyOptions={specialtyOptions}
            specialty={props.specialty}
            onSpecialtySelect={props.onSpecialtySelect}
            isRequired={props.isSpecialtyRequired}
            isValid={props.isSpecialtyValid}
        />
    );

    const children: JSX.Element[] = [
        { ...props.renderItemContainer?.(organizationComboBox, 0) ?? organizationComboBox, key: "OrganizationComboBox" }
    ];

    if (!IsNullOrWhiteSpace(props.selectedOrganizationIdentifier)) {
        children.push({ ...props.renderItemContainer?.(titleComboBox, 1) ?? titleComboBox, key: "TitleComboBox" });
    }

    if (!!props.title && specialtyOptions.any()) {
        children.push({ ...props.renderItemContainer?.(specialtyComboBox, 2) ?? specialtyComboBox, key: "SpecialtyComboBox" });
    }

    return props.renderContainer?.(children) ?? (<>{children}</>);
}

export { TitleAndSpecialtySelection };
